import {
  CEFR_SUBLEVELS,
  type CefrSublevel,
  DIALECTS,
  type Dialect,
  ITEM_TYPES,
  type ItemType,
  LANGUAGES,
  type Language,
  MODALITIES,
  type Modality,
  PILLARS,
  type Pillar,
} from '@saysooner/core';
import { z } from 'zod';

/**
 * The content model.
 *
 * Content is the product. The competitor ships 120-odd lessons with no dialect
 * variants, one store-listing language across 25 markets, and literal `Option 1` /
 * `Option 2` placeholder strings in production — all of which say the content pipeline
 * has no gate. Here every artefact is validated against these schemas, a
 * native-speaker review is a required field rather than a process everyone remembers,
 * and the validator refuses to publish anything that smells like a placeholder.
 */

export const LanguageSchema = z.enum(LANGUAGES);
export const DialectSchema = z.enum(DIALECTS);
export const CefrSublevelSchema = z.enum(CEFR_SUBLEVELS);
export const ItemTypeSchema = z.enum(ITEM_TYPES);
export const PillarSchema = z.enum(PILLARS);
export const ModalitySchema = z.enum(MODALITIES);

/** Where a piece of content is in its lifecycle. */
export const ContentStatusSchema = z.enum([
  'draft',
  'needs_review',
  /** A native speaker of the specific dialect has signed off. */
  'approved',
  'published',
  /** Pulled because it was wrong, not because it was old. */
  'retracted',
]);
export type ContentStatus = z.infer<typeof ContentStatusSchema>;

/**
 * Native-speaker sign-off. Required on anything published, and recorded per dialect:
 * a Castilian reviewer cannot approve Rioplatense content, because the whole point of
 * shipping dialects is that they differ.
 */
export const ReviewSchema = z.object({
  reviewerId: z.string().min(1),
  /** The reviewer's own native dialect, which must match the content's. */
  reviewerDialect: DialectSchema,
  reviewedAt: z.number().int().positive(),
  verdict: z.enum(['approved', 'changes_requested', 'rejected']),
  notes: z.string().max(2000).optional(),
});
export type Review = z.infer<typeof ReviewSchema>;

/**
 * An R2 object key for content audio.
 *
 * Content-hash addressed under `content/`, so a line is synthesised once ever and the
 * Worker can serve it immutably (docs/00-product-strategy.md §9). `utterances/` is
 * reserved for learner recordings, which are private to one learner — content must
 * never be filed there, or it becomes unreadable to everyone else.
 */
export const AudioKeySchema = z
  .string()
  .min(1)
  .max(200)
  .regex(/^content\/[A-Za-z0-9._\-/]+$/, 'must be an R2 key under content/, not a URL');

/** A vocabulary or phrase item — the unit the SRS schedules. */
export const ItemSchema = z.object({
  id: z.string().min(1),
  type: ItemTypeSchema,
  language: LanguageSchema,
  dialect: DialectSchema,
  cefr: CefrSublevelSchema,
  /** The target-language form. */
  text: z.string().min(1).max(200),
  /** English gloss. Other UI languages are translated from this. */
  gloss: z.string().min(1).max(200),
  ipa: z.string().max(200).optional(),
  /** Layman respelling, shown before IPA because most learners cannot read IPA. */
  respelling: z.string().max(200).optional(),
  /** A sentence the item actually appears in, never an invented frame. */
  example: z.string().min(1).max(400),
  exampleGloss: z.string().min(1).max(400),
  /**
   * R2 object key for the reference recording, not a URL.
   *
   * Content used to carry `https://cdn.saysooner.com/audio/...`, which is wrong twice
   * over: that host is not registered, and audio is deliberately proxied through the
   * Worker so retention policy and ownership are enforced on every read
   * (docs/07-tech-architecture.md §9). The API turns a key into `/v1/audio/<key>` at
   * serve time, so the key is the only environment-independent thing to store.
   */
  audioKey: AudioKeySchema.optional(),
  /** Frequency rank in the target language; drives teaching order. */
  frequencyRank: z.number().int().positive().optional(),
  tags: z.array(z.string().min(1)).max(12).default([]),
  /** Which modalities this item can be practised in. */
  modalities: z.array(ModalitySchema).min(1),
  status: ContentStatusSchema,
  reviews: z.array(ReviewSchema).default([]),
  updatedAt: z.number().int().positive(),
});
export type Item = z.infer<typeof ItemSchema>;

/** One prompt-and-response exchange inside a lesson. */
export const LessonTurnSchema = z.object({
  index: z.number().int().nonnegative(),
  prompt: z.string().min(1).max(400),
  promptGloss: z.string().min(1).max(400),
  /** What a correct spoken answer looks like. Absent for open turns. */
  expected: z.string().max(400).optional(),
  expectedIpa: z.string().max(400).optional(),
  expectedRespelling: z.string().max(400).optional(),
  /** Shown after silence rather than leaving the learner on a dead screen. */
  hint: z.string().max(300).optional(),
  requiresVoice: z.boolean().default(true),
  /** Items this turn practises, so the SRS knows what was exercised. */
  itemIds: z.array(z.string().min(1)).default([]),
  audioKey: AudioKeySchema.optional(),
});
export type LessonTurn = z.infer<typeof LessonTurnSchema>;

export const LessonSchema = z.object({
  id: z.string().min(1),
  moduleId: z.string().min(1),
  language: LanguageSchema,
  dialect: DialectSchema,
  cefr: CefrSublevelSchema,
  title: z.string().min(1).max(120),
  /** What the learner will be able to do afterwards, in their words. */
  goal: z.string().min(1).max(200),
  sortOrder: z.number().int().nonnegative(),
  estimatedSeconds: z.number().int().positive().max(1800),
  requiresVoice: z.boolean().default(true),
  pillars: z.array(PillarSchema).min(1),
  turns: z.array(LessonTurnSchema).min(3).max(20),
  status: ContentStatusSchema,
  reviews: z.array(ReviewSchema).default([]),
  updatedAt: z.number().int().positive(),
});
export type Lesson = z.infer<typeof LessonSchema>;

export const ModuleSchema = z.object({
  id: z.string().min(1),
  language: LanguageSchema,
  dialect: DialectSchema,
  title: z.string().min(1).max(120),
  goal: z.string().min(1).max(240),
  cefr: CefrSublevelSchema,
  sortOrder: z.number().int().nonnegative(),
  /** Every module ends with a conversation that uses everything in it. */
  capstoneScenarioId: z.string().min(1).optional(),
  status: ContentStatusSchema,
  updatedAt: z.number().int().positive(),
});
export type Module = z.infer<typeof ModuleSchema>;

/**
 * A conversation scenario.
 *
 * Titles are mundane and specific — "return a sweater that doesn't fit" — because
 * "practise free conversation" is an invitation to freeze. The research on the market
 * leaders is unambiguous on this (docs/research/02-market-map.md §4).
 */
export const ScenarioSchema = z.object({
  id: z.string().min(1),
  language: LanguageSchema,
  dialect: DialectSchema,
  cefr: CefrSublevelSchema,
  title: z.string().min(1).max(120),
  /** The complication that makes it worth practising. */
  detail: z.string().min(1).max(240),
  /** What the tutor is playing. */
  role: z.string().min(1).max(120),
  /**
   * What the *learner* is playing.
   *
   * Separate from `objective` on purpose: a role-play where the learner does not know
   * who they are supposed to be is the "practise free conversation" failure in
   * miniature. The card shows both roles before the first turn.
   */
  yourRole: z.string().min(1).max(120),
  /** What the learner is trying to achieve; used to score completion. */
  objective: z.string().min(1).max(240),
  estimatedMinutes: z.number().int().positive().max(30),
  /** Vocabulary the scenario is designed to exercise. */
  targetItemIds: z.array(z.string().min(1)).default([]),
  /** Turns the tutor should steer toward, so a conversation has a shape. */
  beats: z.array(z.string().min(1).max(200)).min(1).max(8),
  tags: z.array(z.string().min(1)).max(12).default([]),
  status: ContentStatusSchema,
  reviews: z.array(ReviewSchema).default([]),
  updatedAt: z.number().int().positive(),
});
export type Scenario = z.infer<typeof ScenarioSchema>;

/** A grammar concept, explained once and referenced from mistakes. */
export const GrammarConceptSchema = z.object({
  id: z.string().min(1),
  language: LanguageSchema,
  /**
   * The dialect this was written for.
   *
   * Grammar is not dialect-neutral, and treating it as such is wedge W3's failure: a
   * learner who chose Mexico was being served Castilian distinción next to the Mexican
   * concept that teaches the opposite. Every authored concept belongs to one course, so
   * this is required here even though the column allows NULL for a concept that genuinely
   * holds across a whole language.
   */
  dialect: DialectSchema,
  cefr: CefrSublevelSchema,
  title: z.string().min(1).max(120),
  /** Two sentences at most. A grammar wall is a grammar nobody reads. */
  explanation: z.string().min(1).max(600),
  examples: z
    .array(
      z.object({
        correct: z.string().min(1).max(240),
        incorrect: z.string().max(240).optional(),
        gloss: z.string().min(1).max(240),
      }),
    )
    .min(1)
    .max(6),
  /** First languages whose speakers most often get this wrong. */
  commonFor: z.array(LanguageSchema).default([]),
  /**
   * The contrast against a specific first language, keyed by that language.
   *
   * `commonFor` says *who* trips over this; this says *why*, in terms of the language
   * they already speak — "English has no gender on nouns, so there is nothing to carry
   * over; the article has to be learned with the word." The API selects the entry for
   * the learner's own L1 and serves it as `l1Contrast`, which is a real pedagogical
   * advantage built once and reused for every learner with that L1.
   *
   * Every language in `commonFor` needs one: naming a language as prone to an error and
   * then having nothing to say to them is worse than not naming them.
   */
  l1Contrasts: z.record(LanguageSchema, z.string().min(1).max(400)).default({}),
  status: ContentStatusSchema,
  updatedAt: z.number().int().positive(),
});
export type GrammarConcept = z.infer<typeof GrammarConceptSchema>;

/**
 * A listening item. These are the weekly "drops" — the freshness mechanic that gives a
 * learner a reason to open the app on a day they have nothing due.
 */
export const ListeningItemSchema = z.object({
  id: z.string().min(1),
  language: LanguageSchema,
  dialect: DialectSchema,
  cefr: CefrSublevelSchema,
  title: z.string().min(1).max(120),
  /**
   * Where this is happening, in one line — "a voicemail from the dentist's receptionist".
   *
   * Listening without a situation is a comprehension test; with one it is the thing the
   * learner is actually afraid of. The player shows it before pressing play.
   */
  context: z.string().min(1).max(200),
  /** Natural speech, not read-aloud textbook audio. */
  transcript: z.string().min(1).max(4000),
  transcriptGloss: z.string().min(1).max(4000),
  /**
   * The transcript cut into timed segments, which is what the player highlights as the
   * audio runs and what makes every word tappable.
   *
   * Timings are *measured*, never estimated: they come from running the synthesised
   * audio back through Whisper (`pnpm content:align`), so a highlight that drifts is a
   * bug rather than the design. Absent until alignment has run, which is why the
   * validator refuses to publish a listening item without them.
   */
  segments: z
    .array(
      z.object({
        startMs: z.number().int().nonnegative(),
        endMs: z.number().int().positive(),
        text: z.string().min(1),
        translation: z.string().min(1),
      }),
    )
    .default([]),
  audioKey: AudioKeySchema,
  durationSeconds: z.number().int().positive().max(600),
  speakerCount: z.number().int().positive().max(6),
  /** Comprehension checks; three at most, so it stays listening not testing. */
  questions: z
    .array(
      z.object({
        prompt: z.string().min(1).max(240),
        options: z.array(z.string().min(1).max(160)).min(2).max(4),
        answerIndex: z.number().int().nonnegative(),
      }),
    )
    .max(3)
    .default([]),
  publishedAt: z.number().int().positive(),
  status: ContentStatusSchema,
  reviews: z.array(ReviewSchema).default([]),
  updatedAt: z.number().int().positive(),
});
export type ListeningItem = z.infer<typeof ListeningItemSchema>;

/** Everything that makes up one language-dialect course. */
export interface ContentBundle {
  readonly language: Language;
  readonly dialect: Dialect;
  readonly modules: readonly Module[];
  readonly lessons: readonly Lesson[];
  readonly items: readonly Item[];
  readonly scenarios: readonly Scenario[];
  readonly grammar: readonly GrammarConcept[];
  readonly listening: readonly ListeningItem[];
}

export type { CefrSublevel, Dialect, ItemType, Language, Modality, Pillar };
